/* eslint-disable max-len */
/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import Modal from 'react-modal';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import { updateField } from '../../../../actions/BookingRequest';
import { ErrorMessage } from '../../../common';
import { TAgent, TPageNotification, TSeries } from '../../../../types';
import { jobModes } from '../../../../consts';
import { TRootState } from '../../../../reducers';
import { loadAgentsforAgency } from '../../../../actions/Agents';
import { ModalConfirmEditSeriesConsultants } from '../../../operations/series/ConfirmEditConsultants';
import { executeHttpRequest } from '../../../../epics/api';


export type TModalAddConsultantSplitsProps = {
    initialConsultantSplits: TConsultantSplit[];
    consultants: TAgent[];
    setConsultantSplits: (newConsultantSplits: TConsultantSplit[]) => void;
    handleClose: () => void;
    isOpen: boolean;
    seriesId?: number;
    seriesFirstJobStart?: string;
    seriesLastJobStart?: string;
    isEditedBySeries?: boolean;
    notification?: any;
    reloadData?: () => void;
    mode?: string;
    disabled?: boolean;
};

export function ModalAddConsultantSplits({
    initialConsultantSplits,
    consultants,
    setConsultantSplits,
    handleClose,
    isOpen,
    seriesId,
    seriesFirstJobStart,
    seriesLastJobStart,
    isEditedBySeries,
    notification,
    reloadData,
    mode,
    disabled = false,
}: TModalAddConsultantSplitsProps) {
    const dispatch = useDispatch();
    const [error, setError] = useState(null);
    const initialFields = Array.from({ length: 4 }, (_, index) =>
        initialConsultantSplits && initialConsultantSplits[index]
            ? initialConsultantSplits[index]
            : { agent: null, split_value: '' }
    );
    const [fields, setFields] = useState(initialFields);
    const [isModalConfirmEditConsultantSplits, setIsModalConfirmEditConsultantSplits] = useState(false);

    const currentDate = new Date;
    const firstJobDate = new Date(seriesFirstJobStart);
    const [selectedDate, setSelectedDate] = useState(currentDate >= firstJobDate ? currentDate : firstJobDate);

    const isEmpty = (fieldsRow: TConsultantSplit) => !fieldsRow.agent && !fieldsRow.split_value;

    const isFilled = (fieldsRow: TConsultantSplit) => !!fieldsRow.agent && !!fieldsRow.split_value;

    useEffect(() => {
        if (isEditedBySeries) {
            executeHttpRequest(`series/${seriesId}/consultant-splits/`)
                .then(response => setFields(response.data.content))
                // eslint-disable-next-line no-console
                .catch(err => console.log(err));
        }
    }, [isEditedBySeries, seriesId]);
    
    const fieldsAreValid = () => {

        // check if at least one row is filled
        const filledRows = Object.values(fields).filter(field => isFilled(field));

        if (!filledRows.length) {
            setError('At least one row must be filled.');
            return false;
        }

        // check if split_value is valid
        const hasInvalidSplitValue = Object.values(fields).some(field  =>(
            !Number.isNaN(parseInt(field.split_value, 10))
            && (parseInt(field.split_value, 10) < 1 || parseInt(field.split_value, 10) > 100)));
        if (hasInvalidSplitValue) {
            setError('Split value should be an integer between 1 and 100.');
            return false;
        }

        // check sum of split values
        const filledFields = Object.values(fields).filter(field => field.agent && field.split_value);
        let totalSplitValue = 0;

        filledFields.forEach(field => {
            totalSplitValue += Number(field.split_value);
        });

        if (totalSplitValue !== 100) {
            setError('Total split value is not equal to 100.');
            return false;
        }

        // check if consultants are not duplicated
        const selectedConsultants = Object.values(fields).filter(field => field.agent && field.split_value).map(field => field.agent);

        if (selectedConsultants.length !== new Set(selectedConsultants).size) {
            setError('Consultants cannot be duplicated.');
            return false;
        }

        return true;
    };

    const isFormValid = () => {
        const invalidFields = Object.values(fields).filter(field => (!isEmpty(field) && !isFilled(field)));

        if (invalidFields.length) {
            setError('All fields in row must be either empty or filled.');
            return false;
        }
        return true;
    };

    const consultantsOptions =  () => {
        const options = consultants.map(consultant => ({
            value: consultant.id,
            label: consultant.full_name,
        }));
        const consultantIds = consultants.map(consultant => consultant.id);
        Object.values(fields)
            .filter(field => field.agent_name && !consultantIds.includes(field.agent))
            .forEach(field => options.push({
                value: field.agent,
                label: field.agent_name,
            }));
        return options;
    };

    const handleFieldChange = (value: number | string, index: number, fieldName: string) => {
        setFields(prevFields => ({
            ...prevFields,
            [index]: {
                ...prevFields[index],
                [fieldName]: value
            }
        }));
    };

    const handleClearFieldsRow = (event: any, index: number) => {
        event.preventDefault();
        setFields(prevFields => ({
            ...prevFields,
            [index]: {
                agent: null,
                split_value: '',
            }
        }));
    };

    const handleDayChange = (date: Date) => {
        setSelectedDate(date);
    };

    const handleOpenModalConfirmEditConsultantSplits = (): void => {
        setIsModalConfirmEditConsultantSplits(true);
    };

    const handleCloseModalConfirmEditConsultantSplits = (): void => {
        setIsModalConfirmEditConsultantSplits(false);
    };

    const handleSave = () => {
        if (isFormValid() && fieldsAreValid()) {
            if (!isEditedBySeries) {
                const newConsultantSplits = Object.values(fields)
                    .filter(field => field.agent && field.split_value);
                setConsultantSplits(newConsultantSplits);

                dispatch(updateField('consultant_splits', newConsultantSplits));
                handleClose();
            } else {
                setError(null);
                handleOpenModalConfirmEditConsultantSplits();
            }
        }
    };

    return (
            <div>
                <Modal
                    className="add-consultant-splits-modal"
                    isOpen={isOpen}
                    onRequestClose={handleClose}
                    contentLabel="Split Consultants"
                    ariaHideApp={false}
                >
                <div className="popup-header">
                    {isEditedBySeries || mode === jobModes.EDIT ? (
                        <h1>Edit Consultant Splits</h1>
                    ) : (
                        <h1>Add Consultant Splits</h1>
                    )}
                </div>
                <form className="consultant-splits-form">
                    <table>
                        <thead>
                        {isEditedBySeries && (
                            <tr>
                                <td className="edit-rates-label">From date</td>
                                <td>
                                    <DatePicker
                                        className="edit-rates-date-field"
                                        selected={selectedDate}
                                        onChange={date => handleDayChange(date)}
                                        showMonthDropdown={true}
                                        showYearDropdown={true}
                                        dropdownMode="select"
                                        dateFormat="dd/MM/yyyy"
                                        minDate={new Date(seriesFirstJobStart)}
                                        maxDate={new Date(seriesLastJobStart)}
                                        calendarStartDay={1}
                                        disabled={disabled}
                                    />
                                </td>
                            </tr>
                        )}
                        <tr>
                            <th>Consultant</th>
                            <th>Split Value</th>
                        </tr>
                        </thead>
                        <tbody>
                        {Object.keys(initialFields).map((key: any) => (
                            <tr key={key}>
                                <td>
                                    <Select
                                        name="consultant"
                                        // @ts-ignore
                                        value={fields[key]?.agent}
                                        onChange={event => {
                                            handleFieldChange(event.value, key, 'agent');
                                        }}
                                        options={consultantsOptions()}
                                        clearable={false}
                                        style={{ width: '300px' }}
                                        placeholder="Select Consultant..."
                                        disabled={disabled}
                                    />
                                </td>
                                <td className="consultant-splits-number-field">
                                    <input
                                        id={`split_value-${key}`}
                                        type="number"
                                        value={fields[key]?.split_value}
                                        onChange={event => {
                                            handleFieldChange(event.target.value, key, 'split_value');
                                        }}
                                        disabled={disabled}
                                    />
                                </td>
                                <td>
                                    <button
                                        type="button"
                                        onClick={event => {
                                            handleClearFieldsRow(event, key);
                                        }}
                                        disabled={disabled}
                                    >
                                        <i
                                            className="icon icon-remove-circle-1 red"
                                        />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    <div className="buttons-container">
                    <button
                        className="btn btn--round-small"
                        onClick={handleSave}
                        type="button"
                        disabled={disabled}
                    >
                        SAVE
                    </button>
                    <button
                        className="btn cancel-btn"
                        onClick={handleClose}
                        type="button"
                    >
                        CANCEL
                    </button>
                </div>
                </form>
                {error ? (<ErrorMessage message={error}/>) : null}
                    {isModalConfirmEditConsultantSplits &&
                        <ModalConfirmEditSeriesConsultants
                            notification={notification}
                            reloadData={reloadData}
                            isOpen={isModalConfirmEditConsultantSplits}
                            handleClose={handleCloseModalConfirmEditConsultantSplits}
                            handleCloseParentModal={handleClose}
                            fields={fields}
                            selectedDate={selectedDate}
                            consultants={consultants}
                            seriesId={seriesId}
                            setError={setError}
                        />
                    }
            </Modal>
            </div>
        );
    }

type TConsultantSplit = {
    agent: number;
    agent_name?: string;
    split_value: string;
};

type TConsultantSplitsProps = {
    initialConsultantSplits: TConsultantSplit[];
    consultants: TAgent[];
    setConsultantSplits: (newConsultantSplits: TConsultantSplit[]) => void;
    mode: string;
    disabled: boolean;
};

export default function ConsultantSplits({
    initialConsultantSplits,
    consultants,
    setConsultantSplits,
    mode,
    disabled,
}: TConsultantSplitsProps) {
    const [isModalAddConsultantSplitsOpen, setIsModalAddConsultantSplitsOpen] = useState(false);

    const handleOpenAddConsultantSplitsModal = () => {
        setIsModalAddConsultantSplitsOpen(true);
    };

    const handleCloseAddConsultantSplitsModal = () => {
        setIsModalAddConsultantSplitsOpen(false);
    };

    return (
        <div>
            <a
                onClick={handleOpenAddConsultantSplitsModal}
                className="add-consultant-splits-link"
            >
                {initialConsultantSplits?.length > 1 ? 'Check or change Consultant split...' : 'Consultant split...'}
            </a>
            {isModalAddConsultantSplitsOpen && (
                <ModalAddConsultantSplits
                    initialConsultantSplits={initialConsultantSplits}
                    consultants={consultants}
                    setConsultantSplits={setConsultantSplits}
                    handleClose={handleCloseAddConsultantSplitsModal}
                    isOpen={isModalAddConsultantSplitsOpen}
                    mode={mode}
                    disabled={disabled}
                />
            )}
        </div>
    );
}

type TModalSeriesConsultantSplits = {
    notification: TPageNotification;
    reloadData: () => void;
    handleClose: (modalName: string) => void;
    isOpen: boolean;
    series: TSeries;
};

export function ModalSeriesConsultantSplits({
    notification,
    reloadData,
    handleClose,
    isOpen,
    series,
}: TModalSeriesConsultantSplits) {

    const dispatch = useDispatch();

    const [consultantSplits, setConsultantSplits] = useState([]);
    const { user, agents, isFetching, agentsLoadingError } = useSelector((state: TRootState) => ({
        user: state.user,
        agents: state.agents.content,
        isFetching: state.agents.fetching,
        agentsLoadingError: state.agents.error,
    }));

    useEffect(() => {
        if (!agents?.length && !isFetching && !agentsLoadingError) {
            dispatch(loadAgentsforAgency(user.agency.id));
        }
    }, []);

    const handleCloseAddConsultantSplitsModal = () => handleClose('SeriesConsultantSplits');

    return (
        <div>
            {isOpen && (
                <ModalAddConsultantSplits
                    initialConsultantSplits={consultantSplits}
                    consultants={agents}
                    setConsultantSplits={setConsultantSplits}
                    handleClose={handleCloseAddConsultantSplitsModal}
                    isOpen={isOpen}
                    seriesId={series.id}
                    seriesFirstJobStart={series.first_job_start}
                    seriesLastJobStart={series.last_job_start}
                    isEditedBySeries={true}
                    notification={notification}
                    reloadData={reloadData}
                    disabled={!(series.managed && series.owned)}
                />
            )}
        </div>
    );
}
